import { useState } from 'react';
import { Link } from 'react-router-dom';

interface Suggestion {
  id: number;
  text: string;
  icon: string;
  applied: boolean;
}

export default function CanvasView() {
  const [chatOpen, setChatOpen] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([
    { id: 1, text: 'Add customer reviews to build trust', icon: 'star', applied: false },
    { id: 2, text: 'Compress images — save 40% bandwidth', icon: 'speed', applied: false },
    { id: 3, text: 'Add opening hours to the homepage', icon: 'schedule', applied: false },
  ]);
  const [chatMessages, setChatMessages] = useState<{ role: 'ai' | 'user'; text: string }[]>([
    { role: 'ai', text: 'Your site looks great! I have a few suggestions to make it even better. Check them out below.' },
  ]);
  const [typing, setTyping] = useState(false);

  const applySuggestion = (id: number) => {
    setSuggestions((prev) =>
      prev.map((s) => (s.id === id ? { ...s, applied: true } : s)),
    );
  };

  const handleChatSend = () => {
    if (!chatInput.trim()) return;
    const text = chatInput.trim();
    setChatInput('');
    setChatMessages((prev) => [...prev, { role: 'user' as const, text }]);
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setChatMessages((prev) => [
        ...prev,
        {
          role: 'ai' as const,
          text: "I'll make that change now. Saving a backup first, then updating your site. You'll see the preview refresh in a moment.",
        },
      ]);
    }, 1200);
  };

  return (
    <div className="h-screen bg-base flex flex-col overflow-hidden">
      {/* Minimal top bar */}
      <div className="h-12 flex items-center justify-between px-4 md:px-6 border-b border-zinc-800 bg-zinc-950/80 shrink-0">
        <div className="flex items-center gap-3">
          <Link to="/mockups" className="text-orange-500 font-bold text-sm hover:text-orange-400 transition">
            WiserWeb
          </Link>
          <span className="text-zinc-700">|</span>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white">Fresh Bakery</span>
            <span className="material-icons text-zinc-600 text-sm cursor-pointer hover:text-zinc-400">
              expand_more
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button className="text-zinc-500 hover:text-zinc-300 transition">
            <span className="material-icons text-lg">history</span>
          </button>
          <span className="text-xs text-zinc-600 hidden sm:inline">v3 saved 2m ago</span>
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-xs font-bold text-white">
            S
          </div>
        </div>
      </div>

      {/* Main content — scrollable */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-6">
          {/* Large site preview */}
          <div className="bg-zinc-800/80 border border-zinc-700/60 rounded-2xl overflow-hidden shadow-2xl mb-6">
            {/* Browser chrome */}
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-zinc-700/40 bg-zinc-900/60">
              <div className="flex gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                <span className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
              </div>
              <div className="flex-1 flex items-center gap-1.5 bg-zinc-800/80 rounded-lg px-3 py-1">
                <span className="material-icons text-green-400 text-xs">lock</span>
                <span className="text-xs text-zinc-400 font-mono">fresh-bakery.wiserwebhost.com</span>
              </div>
              <a
                href="#"
                className="text-xs text-orange-400 hover:text-orange-300 flex items-center gap-1 transition"
              >
                <span className="material-icons text-sm">open_in_new</span>
                Visit
              </a>
            </div>

            {/* Fake site — larger preview */}
            <div className="p-6 md:p-8 bg-zinc-900/30">
              {/* Hero */}
              <div className="h-40 md:h-56 bg-gradient-to-br from-amber-800/30 to-orange-700/30 rounded-xl flex items-center justify-center mb-6">
                <div className="text-center">
                  <p className="text-2xl md:text-3xl font-bold text-orange-200/80">Fresh Bakery</p>
                  <p className="text-sm text-orange-300/40 mt-2">Handcrafted artisan breads & pastries since 2018</p>
                  <button className="mt-4 px-4 py-1.5 bg-orange-500/20 border border-orange-500/40 rounded-lg text-xs text-orange-300">
                    Order Now
                  </button>
                </div>
              </div>

              {/* Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {['Sourdough', 'Croissants', 'Pastries', 'Cakes'].map((item) => (
                  <div key={item} className="bg-zinc-800/40 rounded-lg p-3">
                    <div className="aspect-square bg-zinc-700/20 rounded mb-2" />
                    <p className="text-xs text-zinc-400 text-center">{item}</p>
                  </div>
                ))}
              </div>

              {/* More blocks */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-zinc-800/30 rounded-lg p-4">
                  <div className="h-2.5 bg-zinc-700/30 rounded w-24 mb-3" />
                  <div className="space-y-1.5">
                    <div className="h-2 bg-zinc-700/20 rounded w-full" />
                    <div className="h-2 bg-zinc-700/20 rounded w-4/5" />
                    <div className="h-2 bg-zinc-700/20 rounded w-3/5" />
                  </div>
                </div>
                <div className="bg-zinc-800/30 rounded-lg p-4">
                  <div className="h-2.5 bg-zinc-700/30 rounded w-20 mb-3" />
                  <div className="space-y-1.5">
                    <div className="h-2 bg-zinc-700/20 rounded w-full" />
                    <div className="h-2 bg-zinc-700/20 rounded w-3/4" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {[
              { icon: 'edit_note', label: 'Edit Content', desc: 'Text, images, pages' },
              { icon: 'palette', label: 'Change Design', desc: 'Colors, fonts, layout' },
              { icon: 'domain', label: 'Custom Domain', desc: 'Connect your domain' },
              { icon: 'analytics', label: 'View Analytics', desc: 'Visitors & traffic' },
            ].map((action) => (
              <button
                key={action.label}
                className="group p-4 bg-zinc-900/60 border border-zinc-800 rounded-xl hover:border-orange-500/40 hover:bg-orange-500/5 transition-all text-left"
              >
                <span className="material-icons text-2xl text-zinc-500 group-hover:text-orange-400 transition mb-2 block">
                  {action.icon}
                </span>
                <p className="text-sm font-medium text-white">{action.label}</p>
                <p className="text-xs text-zinc-500 mt-0.5">{action.desc}</p>
              </button>
            ))}
          </div>

          {/* AI Suggestions strip */}
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="material-icons text-orange-400 text-lg">auto_awesome</span>
              <h3 className="text-sm font-medium text-white">AI Suggestions</h3>
            </div>
            <div className="space-y-2">
              {suggestions.map((s) => (
                <div
                  key={s.id}
                  className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg transition ${
                    s.applied
                      ? 'bg-emerald-500/10 border border-emerald-500/20'
                      : 'bg-zinc-800/40 border border-zinc-700/30 hover:border-zinc-600'
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className={`material-icons text-sm ${
                        s.applied ? 'text-emerald-400' : 'text-zinc-500'
                      }`}
                    >
                      {s.applied ? 'check_circle' : s.icon}
                    </span>
                    <span
                      className={`text-sm ${
                        s.applied ? 'text-emerald-300 line-through' : 'text-zinc-300'
                      }`}
                    >
                      {s.text}
                    </span>
                  </div>
                  {!s.applied && (
                    <button
                      onClick={() => applySuggestion(s.id)}
                      className="shrink-0 px-3 py-1 bg-orange-500/20 hover:bg-orange-500/30 border border-orange-500/30 rounded-md text-xs text-orange-300 transition"
                    >
                      Apply
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Backups row */}
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="material-icons text-zinc-500 text-lg">backup</span>
                <h3 className="text-sm font-medium text-white">Backups</h3>
              </div>
              <span className="text-xs text-zinc-600">Auto-saved every change</span>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {[
                { label: 'v3 — Current', time: '2 min ago', active: true },
                { label: 'v2 — Added menu', time: '1 hour ago', active: false },
                { label: 'v1 — Initial build', time: 'Yesterday', active: false },
              ].map((v) => (
                <div
                  key={v.label}
                  className={`shrink-0 px-3 py-2 rounded-lg border text-xs cursor-pointer transition ${
                    v.active
                      ? 'bg-orange-500/10 border-orange-500/30 text-orange-300'
                      : 'bg-zinc-800/40 border-zinc-700/40 text-zinc-400 hover:border-zinc-600'
                  }`}
                >
                  <p className="font-medium">{v.label}</p>
                  <p className="text-zinc-600 mt-0.5">{v.time}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Floating chat button */}
      {!chatOpen && (
        <button
          onClick={() => setChatOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center shadow-glow hover:shadow-glow-lg transition-all hover:-translate-y-0.5 z-40"
        >
          <span className="material-icons text-white text-xl">chat</span>
        </button>
      )}

      {/* Chat drawer */}
      {chatOpen && (
        <div className="fixed bottom-6 right-6 w-[360px] max-w-[calc(100vw-3rem)] h-[480px] max-h-[calc(100vh-6rem)] bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl flex flex-col z-50 overflow-hidden">
          {/* Chat header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
            <div className="flex items-center gap-2">
              <span className="material-icons text-orange-400 text-lg">smart_toy</span>
              <span className="text-sm font-medium text-white">AI Assistant</span>
            </div>
            <button
              onClick={() => setChatOpen(false)}
              className="text-zinc-500 hover:text-zinc-300 transition"
            >
              <span className="material-icons text-lg">close</span>
            </button>
          </div>

          {/* Chat messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {chatMessages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                    m.role === 'user'
                      ? 'bg-orange-500/15 border border-orange-500/30 rounded-br-md text-orange-200'
                      : 'bg-zinc-800/60 border border-zinc-700/50 rounded-bl-md text-zinc-300'
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {typing && (
              <div className="flex justify-start">
                <div className="bg-zinc-800/60 border border-zinc-700/50 rounded-2xl rounded-bl-md px-4 py-3">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce" />
                    <span className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Chat input */}
          <div className="p-3 border-t border-zinc-800">
            <div className="flex items-center gap-2 bg-zinc-800/60 border border-zinc-700/50 rounded-lg px-3">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleChatSend()}
                placeholder="Ask anything..."
                className="flex-1 bg-transparent py-2 text-sm text-white placeholder:text-zinc-600 outline-none"
              />
              <button onClick={handleChatSend} className="text-orange-400 hover:text-orange-300 transition">
                <span className="material-icons text-lg">send</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
